import React from 'react';
import { HStack, VStack, IconButton, Text, Tooltip, Box, Divider } from '@chakra-ui/react';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebook } from 'react-icons/fa';
import { FiMail } from 'react-icons/fi';
import NextLink from 'next/link';
import useSocialLogin from '../SocialLoginButton';
import InfoTooltip from 'src/components/atoms/info-tooltip';
import useSSRSafeColorModeValue from 'src/utils/hooks/useSSRSafeColorModeValue';
import { useRouter } from 'next/router';
import { useOnboardingTheme } from 'src/contexts/onboardingTheme';

interface SocialLoginRowProps {
  isRegister: boolean;
  onClose?: () => void;
  showEmailLogin?: boolean;
  resetPasswordToken?: string;
  showDivider?: boolean;
  returnUrl?: string;
}

interface LoginButtonProps {
  icon: React.ReactElement;
  label: string;
  onClick?: (event: React.MouseEvent) => void | Promise<void>;
  href?: string;
  tooltipLabel?: string;
  showTooltip?: boolean;
  bgColor?: string;
  isDisabled?: boolean;
  dataTest?: string;
}

const LoginButton: React.FC<LoginButtonProps> = ({
  icon,
  label,
  onClick,
  href,
  tooltipLabel,
  showTooltip = false,
  bgColor = 'white',
  isDisabled = false,
  dataTest,
}) => {
  const buttonProps = {
    'aria-label': label,
    icon,
    size: 'lg',
    variant: 'outline',
    borderRadius: 'full',
    bg: bgColor,
    border: '2px solid',
    borderColor: useSSRSafeColorModeValue('gray.200', 'gray.600'),
    w: { base: '48px', md: '64px' },
    h: { base: '48px', md: '64px' },
    fontSize: { base: '30px', md: '40px' },
    _hover: {
      transform: 'scale(1.05)',
      borderColor: useSSRSafeColorModeValue('gray.300', 'gray.500'),
    },
    _active: {
      transform: 'scale(0.95)',
    },
    isDisabled,
    'data-test': dataTest,
  };

  const button = href ? (
    <IconButton as={NextLink} href={href} onClick={onClick} {...buttonProps} />
  ) : (
    <IconButton onClick={onClick} {...buttonProps} />
  );

  return (
    <VStack spacing={2} align="center">
      <Tooltip label={tooltipLabel} isOpen={showTooltip} hasArrow>
        {button}
      </Tooltip>
      <Text fontSize="xs" color="secondaryText" fontWeight="medium" textAlign="center">
        {label}
      </Text>
    </VStack>
  );
};

const SocialLoginRow: React.FC<SocialLoginRowProps> = ({
  isRegister,
  onClose,
  showEmailLogin = false,
  resetPasswordToken,
  showDivider = true,
  returnUrl,
}) => {
  const router = useRouter();
  const isAdminRoute = router.pathname.startsWith('/slug_admin');
  const { isOnboarding } = useOnboardingTheme();
  const googleLogin = useSocialLogin({
    provider: 'google-sso',
    isRegister,
    onClose,
    resetPasswordToken,
    returnUrl,
  });

  const facebookLogin = useSocialLogin({
    provider: 'facebook-sso',
    isRegister,
    onClose,
    resetPasswordToken,
    returnUrl,
  });

  const upIn = isRegister ? 'Sign up' : 'Log in';

  return (
    <Box px={8} py={6}>
      {/* Social Login Buttons Row */}
      <VStack spacing={6}>
        <VStack spacing={4}>
          <HStack spacing={{ base: 6, md: 8 }} justify="center">
            {/* Google Login */}
            <LoginButton
              icon={<FcGoogle />}
              label="Google"
              onClick={googleLogin.handleSignIn}
              tooltipLabel={`To ${upIn.toLowerCase()} with Google, please open this link in your dedicated mobile browser.`}
              showTooltip={googleLogin.showTooltip}
              isDisabled={!googleLogin.marketplace && !isAdminRoute}
              dataTest="google-login-btn"
            />

            {/* Facebook Login */}
            <LoginButton
              icon={<FaFacebook color="#1877F2" />}
              label="Facebook"
              onClick={facebookLogin.handleSignIn}
              tooltipLabel={`To ${upIn.toLowerCase()} with Facebook, please open this link in your dedicated mobile browser.`}
              showTooltip={facebookLogin.showTooltip}
              isDisabled={!facebookLogin.marketplace && !isAdminRoute}
              dataTest="facebook-login-btn"
            />

            {/* Email Login (only for login, not signup) */}
            {showEmailLogin && !isRegister && (
              <LoginButton
                icon={<FiMail />}
                label="Email Link"
                href="/email-login"
                onClick={onClose}
                dataTest="email-login-btn"
              />
            )}
          </HStack>

          {!isOnboarding && (
            <Box textAlign="center" pt={4}>
              <Text fontSize="xs" color="secondaryText">
                Powered by <b>Loma</b>
                <InfoTooltip
                  description="We use Loma for our commerce platform and user verification."
                  boxSize={3}
                />
              </Text>
            </Box>
          )}
        </VStack>

        {showDivider && (
          <HStack width="100%" maxW="md">
            <Divider />
            <Text fontSize="sm" color="secondaryText" whiteSpace="nowrap" px={2}>
              or
            </Text>
            <Divider />
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

export default SocialLoginRow;
